import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Shield,
  Users as UsersIcon,
  Store,
  Package,
  Receipt,
  BarChart3,
  Ban,
  CheckCircle2,
  Star,
  Trash2,
  Search as SearchIcon,
  RefreshCw,
  BadgeCheck,
  EyeOff,
  Eye,
  Crown,
  LifeBuoy,
  FileText,
  UserPlus,
  UserMinus,
  Send,
  ChevronLeft,
  AlertTriangle,
  DollarSign,
  Rocket,
  Radio,
  Activity,
  Bell,
  Megaphone,
  ChevronRight,
  Zap,
  MessageSquare,
} from "lucide-react";
import { StaffPortalTab } from "./admin/StaffPortalTab";
import { VendorDeepProfile } from "./admin/VendorDeepProfile";
import { TrashTab } from "./admin/TrashTab";

const BASE_TABS = [
  { key: "operations", label: "Ops", icon: Activity },
  { key: "staff_portal", label: "Staff Chat", icon: MessageSquare },
  { key: "stats", label: "Overview", icon: BarChart3 },
  { key: "subscriptions", label: "Subs", icon: DollarSign },
  { key: "tickets", label: "Tickets", icon: LifeBuoy },
  { key: "users", label: "Users", icon: UsersIcon },
  { key: "vendors", label: "Vendors", icon: Store },
  { key: "products", label: "Products", icon: Package },
  { key: "orders", label: "Orders", icon: Receipt },
  { key: "logs", label: "Mod Logs", icon: FileText },
  { key: "trash", label: "Trash", icon: Trash2 },
];
const CEO_TABS = [
  { key: "roles", label: "Roles", icon: Crown },
  { key: "admins", label: "Admins", icon: Crown },
];

function StatCard({ label, value, accent = "pink", pulse = false, hint = null, onClick = null }) {
  const ring =
    accent === "cyan"
      ? "from-cyan-400/15 border-cyan-400/30 text-cyan-200"
      : accent === "amber"
      ? "from-amber-400/15 border-amber-400/30 text-amber-200"
      : accent === "red"
      ? "from-red-500/15 border-red-400/40 text-red-200"
      : accent === "emerald"
      ? "from-emerald-400/15 border-emerald-400/30 text-emerald-200"
      : "from-pink-500/15 border-pink-400/30 text-pink-200";
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick || undefined}
      className={`relative rounded-2xl bg-gradient-to-br ${ring} to-transparent border p-4 text-left transition-transform ${onClick ? "hover:scale-[1.02] active:scale-[0.98] w-full" : ""}`}
      data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {pulse && value > 0 && (
        <span className="absolute top-3 right-3 flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-400"></span>
        </span>
      )}
      <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</p>
      <p className="mt-1 text-2xl font-black tracking-tighter">{value}</p>
      {hint && <p className="text-[10px] text-zinc-500 mt-0.5">{hint}</p>}
    </Tag>
  );
}

function StatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !stats) {
    return <div className="text-zinc-400 text-sm" data-testid="admin-stats-loading">Loading…</div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-stats">
      {/* PLATFORM */}
      <div>
        <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Platform</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Users" value={stats.users.total} />
          <StatCard label="Vendors" value={stats.users.vendors} accent="cyan" />
          <StatCard label="Banned" value={stats.users.banned} accent="amber" />
          <StatCard label="Pending Vendors" value={stats.users.pending_vendors} accent="amber" pulse />
        </div>
      </div>

      {/* COMMUNITY */}
      <div>
        <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Community</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StatCard label="Products" value={stats.products.total} />
          <StatCard label="Active" value={stats.products.active} accent="cyan" />
          <StatCard label="Featured" value={stats.products.featured} accent="amber" />
        </div>
      </div>

      {/* REVENUE / ORDERS */}
      <div>
        <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Revenue · Orders</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Orders" value={stats.orders.total} />
          <StatCard label="Pending" value={stats.orders.pending} accent="amber" />
          <StatCard label="Completed" value={stats.orders.completed} accent="cyan" />
          <StatCard label="Revenue" value={`$${stats.orders.revenue.toFixed(2)}`} accent="emerald" />
        </div>
      </div>

      <Button
        onClick={load}
        className="rounded-full bg-white/5 hover:bg-white/10 border border-white/10"
        data-testid="admin-stats-refresh"
      >
        <RefreshCw className="w-4 h-4 mr-2" /> Refresh
      </Button>
    </div>
  );
}

function UsersTab() {
  const { user: me } = useAuth();
  const isMaster = me?.role === "ceo" || me?.role === "master_admin";
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { q, limit: 100 } });
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleBan = async (u) => {
    const banned = !u.is_banned;
    try {
      await api.post(`/admin/users/${u.user_id}/ban`, null, { params: { banned } });
      toast.success(banned ? "User banned" : "User unbanned");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-users">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email…"
            className="pl-9 h-11 rounded-full bg-white/5 border-white/10"
            data-testid="admin-users-search"
          />
        </div>
        <Button
          onClick={load}
          className="rounded-full bg-white/5 hover:bg-white/10 border border-white/10"
          data-testid="admin-users-refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      <div className="space-y-2">
        {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
        {!loading && users.length === 0 && (
          <p className="text-zinc-500 text-sm">No users found.</p>
        )}
        {users.map((u) => (
          <div
            key={u.user_id}
            className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10"
            data-testid={`admin-user-row-${u.user_id}`}
          >
            <div className="w-10 h-10 rounded-full bg-pink-500/20 grid place-items-center text-pink-200 font-bold">
              {(u.name || u.email).slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{u.name}</p>
              <p className="text-xs text-zinc-400 truncate">{u.email}</p>
              <div className="mt-1 flex gap-1.5 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                  {u.role}
                </span>
                {u.is_vendor && (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-cyan-400/15 text-cyan-200 border border-cyan-400/30">
                    Vendor
                  </span>
                )}
                {u.is_banned && (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/15 text-red-200 border border-red-400/30">
                    Banned
                  </span>
                )}
              </div>
            </div>
            <Button
              onClick={() => toggleBan(u)}
              disabled={u.role === "master_admin" || (u.role === "admin" && !isMaster)}
              className={`rounded-full h-9 px-3 text-xs ${
                u.is_banned
                  ? "bg-cyan-400/15 hover:bg-cyan-400/25 text-cyan-200 border border-cyan-400/30"
                  : "bg-amber-400/15 hover:bg-amber-400/25 text-amber-200 border border-amber-400/30"
              }`}
              data-testid={`admin-user-ban-${u.user_id}`}
            >
              <Ban className="w-3.5 h-3.5 mr-1" /> {u.is_banned ? "Unban" : "Ban"}
            </Button>
            {isMaster && (
              <Button
                onClick={() => remove(u)}
                disabled={u.role === "master_admin"}
                className="rounded-full h-9 px-3 text-xs bg-red-500/15 hover:bg-red-500/25 text-red-200 border border-red-400/30"
                data-testid={`admin-user-delete-${u.user_id}`}
                title="Master Admin · permanent delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function VendorsTab({ onOpenProfile }) {
  const [vendors, setVendors] = useState([]);
  const [pending, setPending] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [allRes, pendRes] = await Promise.all([
        api.get("/admin/vendors", { params: { q, limit: 100 } }),
        api.get("/admin/vendors", { params: { approved: false, limit: 50 } }),
      ]);
      setVendors(allRes.data);
      setPending(pendRes.data.filter((v) => v.is_suspended !== true));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load vendors");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  const setApproved = async (v, approved) => {
    try {
      await api.post(`/admin/vendors/${v.user_id}/approve`, null, { params: { approved } });
      toast.success(approved ? "Vendor approved ✨" : "Vendor un-approved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed");
    }
  };

  const reject = async (v) => {
    const reason = window.prompt(`Reject vendor application from ${v.email}?\n\nOptional reason for the applicant:`);
    if (reason === null) return;
    try {
      await api.post(`/admin/vendors/${v.user_id}/reject`, null, { params: { reason } });
      toast.success("Application rejected");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed");
    }
  };

  const setVerified = async (v, verified) => {
    try {
      await api.post(`/admin/vendors/${v.user_id}/verify`, null, { params: { verified } });
      toast.success(verified ? "Marked verified" : "Verification removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed");
    }
  };

  const setSuspended = async (v, suspended) => {
    try {
      await api.post(`/admin/vendors/${v.user_id}/suspend`, null, { params: { suspended } });
      toast.success(suspended ? "Vendor suspended" : "Vendor reinstated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed");
    }
  };

  const setVip = async (v, vip) => {
    if (!window.confirm(
      vip
        ? `Grant VIP to ${v.vendor_profile?.business_name || v.name}? They'll stop paying subscription fees until VIP is removed. Only they will see this status.`
        : `Remove VIP from ${v.vendor_profile?.business_name || v.name}? They'll be moved back to the regular subscription flow.`
    )) return;
    try {
      await api.post(`/admin/vendors/${v.user_id}/vip`, null, { params: { vip } });
      toast.success(vip ? "VIP granted" : "VIP removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-vendors">
      {pending.length > 0 && (
        <div className="rounded-2xl bg-gradient-to-br from-amber-400/15 to-transparent border-2 border-amber-400/40 p-4 space-y-3" data-testid="admin-vendor-applications">
          <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black inline-flex items-center gap-1.5">
            <Crown className="w-3 h-3" /> Vendor Applications ({pending.length})
          </p>
          {pending.map((v) => {
            const profile = v.vendor_profile || {};
            return (
              <div key={v.user_id} className="rounded-xl bg-black/30 border border-amber-400/30 p-3" data-testid={`admin-vendor-pending-${v.user_id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-400/15 grid place-items-center text-amber-200 font-bold border border-amber-400/30">
                    {(profile.business_name || v.name).slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold truncate">{profile.business_name || v.name}</p>
                    <p className="text-xs text-zinc-400 truncate">{v.email} · {profile.vendor_type || "—"} · {profile.location || "—"}</p>
                    {profile.bio && <p className="mt-1.5 text-xs text-zinc-300 line-clamp-2">{profile.bio}</p>}
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button
                    onClick={() => setApproved(v, true)}
                    className="rounded-full h-9 px-4 text-xs bg-emerald-400/15 hover:bg-emerald-400/25 text-emerald-200 border border-emerald-400/30 font-bold"
                    data-testid={`admin-vendor-approve-pending-${v.user_id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                  <Button
                    onClick={() => reject(v)}
                    className="rounded-full h-9 px-4 text-xs bg-red-500/15 hover:bg-red-500/25 text-red-200 border border-red-400/30 font-bold"
                    data-testid={`admin-vendor-reject-${v.user_id}`}
                  >
                    <Ban className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search vendors…"
            className="pl-9 h-11 rounded-full bg-white/5 border-white/10"
            data-testid="admin-vendors-search"
          />
        </div>
        <Button
          onClick={load}
          className="rounded-full bg-white/5 hover:bg-white/10 border border-white/10"
          data-testid="admin-vendors-refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      <div className="space-y-2">
        {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
        {!loading && vendors.length === 0 && (
          <p className="text-zinc-500 text-sm">No vendors found.</p>
        )}
        {vendors.map((v) => {
          const profile = v.vendor_profile || {};
          const suspended = v.is_suspended || v.is_approved === false;
          return (
            <div
              key={v.user_id}
              className="p-3 rounded-xl bg-white/5 border border-white/10"
              data-testid={`admin-vendor-row-${v.user_id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-cyan-400/20 grid place-items-center text-cyan-200 font-bold">
                  {(profile.business_name || v.name).slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{profile.business_name || v.name}</p>
                  <p className="text-xs text-zinc-400 truncate">
                    {v.email} · {profile.vendor_type || "—"} · {profile.location || "—"}
                  </p>
                  <div className="mt-1 flex gap-1.5 flex-wrap">
                    {v.is_verified && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-cyan-400/15 text-cyan-200 border border-cyan-400/30">
                        Verified
                      </span>
                    )}
                    {v.is_vip && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-300 to-pink-500 text-black font-black" data-testid={`admin-vendor-vip-pill-${v.user_id}`}>
                        VIP
                      </span>
                    )}
                    {suspended ? (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/15 text-red-200 border border-red-400/30">
                        Suspended
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-200 border border-emerald-400/30">
                        Active
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {suspended ? (
                  <Button
                    onClick={() => setSuspended(v, false)}
                    className="rounded-full h-8 px-3 text-xs bg-emerald-400/15 hover:bg-emerald-400/25 text-emerald-200 border border-emerald-400/30"
                    data-testid={`admin-vendor-reinstate-${v.user_id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Reinstate
                  </Button>
                ) : (
                  <Button
                    onClick={() => setSuspended(v, true)}
                    className="rounded-full h-8 px-3 text-xs bg-amber-400/15 hover:bg-amber-400/25 text-amber-200 border border-amber-400/30"
                    data-testid={`admin-vendor-suspend-${v.user_id}`}
                  >
                    <Ban className="w-3.5 h-3.5 mr-1" /> Suspend
                  </Button>
                )}
                {v.is_verified ? (
                  <Button
                    onClick={() => setVerified(v, false)}
                    className="rounded-full h-8 px-3 text-xs bg-white/5 hover:bg-white/10 border border-white/10"
                    data-testid={`admin-vendor-unverify-${v.user_id}`}
                  >
                    Remove verify
                  </Button>
                ) : (
                  <Button
                    onClick={() => setVerified(v, true)}
                    className="rounded-full h-8 px-3 text-xs bg-cyan-400/15 hover:bg-cyan-400/25 text-cyan-200 border border-cyan-400/30"
                    data-testid={`admin-vendor-verify-${v.user_id}`}
                  >
                    <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Verify
                  </Button>
                )}
                {v.is_approved === false && (
                  <Button
                    onClick={() => setApproved(v, true)}
                    className="rounded-full h-8 px-3 text-xs bg-pink-500/15 hover:bg-pink-500/25 text-pink-200 border border-pink-400/30"
                    data-testid={`admin-vendor-approve-${v.user_id}`}
                  >
                    Approve
                  </Button>
                )}
                {v.is_vip ? (
                  <Button
                    onClick={() => setVip(v, false)}
                    className="rounded-full h-8 px-3 text-xs bg-white/5 hover:bg-white/10 border border-white/10"
                    data-testid={`admin-vendor-vip-remove-${v.user_id}`}
                  >
                    Remove VIP
                  </Button>
                ) : (
                  <Button
                    onClick={() => setVip(v, true)}
                    className="rounded-full h-8 px-3 text-xs bg-gradient-to-r from-amber-300/20 to-pink-500/20 hover:from-amber-300/30 hover:to-pink-500/30 text-amber-100 border border-amber-400/40 font-bold"
                    data-testid={`admin-vendor-vip-grant-${v.user_id}`}
                  >
                    <Crown className="w-3.5 h-3.5 mr-1" /> Grant VIP
                  </Button>
                )}
                {onOpenProfile && (
                  <Button
                    onClick={() => onOpenProfile(v.user_id)}
                    className="rounded-full h-8 px-3 text-xs bg-pink-500/15 hover:bg-pink-500/25 text-pink-200 border border-pink-400/30 font-bold"
                    data-testid={`admin-vendor-open-profile-${v.user_id}`}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" /> Profile
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProductsTab() {
  const { user: me } = useAuth();
  const isMaster = me?.role === "ceo" || me?.role === "master_admin";
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products", { params: { q, limit: 100 } });
      setProducts(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  const setFeatured = async (p, featured) => {
    try {
      await api.post(`/admin/products/${p.product_id}/feature`, null, { params: { featured } });
      toast.success(featured ? "Featured" : "Removed from featured");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed");
    }
  };

  const setActive = async (p, active) => {
    try {
      await api.post(`/admin/products/${p.product_id}/active`, null, { params: { active } });
      toast.success(active ? "Product shown" : "Product hidden");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.product_id}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-products">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products…"
            className="pl-9 h-11 rounded-full bg-white/5 border-white/10"
            data-testid="admin-products-search"
          />
        </div>
        <Button
          onClick={load}
          className="rounded-full bg-white/5 hover:bg-white/10 border border-white/10"
          data-testid="admin-products-refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      <div className="space-y-2">
        {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
        {!loading && products.length === 0 && (
          <p className="text-zinc-500 text-sm">No products found.</p>
        )}
        {products.map((p) => (
          <div
            key={p.product_id}
            className="p-3 rounded-xl bg-white/5 border border-white/10"
            data-testid={`admin-product-row-${p.product_id}`}
          >
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{p.name}</p>
                <p className="text-xs text-zinc-400 truncate">
                  ${Number(p.price || 0).toFixed(2)} · {p.category}
                </p>
                <div className="mt-1 flex gap-1.5 flex-wrap">
                  {p.is_featured && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-200 border border-amber-400/30">
                      Featured
                    </span>
                  )}
                  {p.is_active ? (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-200 border border-emerald-400/30">
                      Active
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-zinc-500/15 text-zinc-300 border border-zinc-400/30">
                      Hidden
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                onClick={() => setFeatured(p, !p.is_featured)}
                className={`rounded-full h-8 px-3 text-xs border ${
                  p.is_featured
                    ? "bg-white/5 hover:bg-white/10 border-white/10"
                    : "bg-amber-400/15 hover:bg-amber-400/25 text-amber-200 border-amber-400/30"
                }`}
                data-testid={`admin-product-feature-${p.product_id}`}
              >
                <Star className="w-3.5 h-3.5 mr-1" /> {p.is_featured ? "Unfeature" : "Feature"}
              </Button>
              <Button
                onClick={() => setActive(p, !p.is_active)}
                className={`rounded-full h-8 px-3 text-xs border ${
                  p.is_active
                    ? "bg-amber-400/15 hover:bg-amber-400/25 text-amber-200 border-amber-400/30"
                    : "bg-emerald-400/15 hover:bg-emerald-400/25 text-emerald-200 border-emerald-400/30"
                }`}
                data-testid={`admin-product-active-${p.product_id}`}
              >
                {p.is_active ? (
                  <>
                    <EyeOff className="w-3.5 h-3.5 mr-1" /> Hide
                  </>
                ) : (
                  <>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Show
                  </>
                )}
              </Button>
              <Button
                onClick={() => remove(p)}
                disabled={!isMaster}
                className="rounded-full h-8 px-3 text-xs bg-red-500/15 hover:bg-red-500/25 text-red-200 border border-red-400/30 disabled:opacity-40"
                data-testid={`admin-product-delete-${p.product_id}`}
                title={isMaster ? "Master Admin · permanent delete" : "Only Master Admin can permanently delete"}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ORDER_STATUSES = ["pending", "paid", "completed", "cancelled", "refunded"];

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders", { params: { status, limit: 100 } });
      setOrders(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (o, next) => {
    try {
      await api.post(`/admin/orders/${o.order_id}/status`, null, { params: { status: next } });
      toast.success(`Order set to ${next}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-orders">
      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={() => setStatus("")}
          className={`rounded-full h-9 px-3 text-xs border ${
            status === "" ? "bg-pink-500/20 text-pink-200 border-pink-400/40" : "bg-white/5 border-white/10"
          }`}
          data-testid="admin-orders-filter-all"
        >
          All
        </Button>
        {ORDER_STATUSES.map((s) => (
          <Button
            key={s}
            onClick={() => setStatus(s)}
            className={`rounded-full h-9 px-3 text-xs border ${
              status === s ? "bg-pink-500/20 text-pink-200 border-pink-400/40" : "bg-white/5 border-white/10"
            }`}
            data-testid={`admin-orders-filter-${s}`}
          >
            {s}
          </Button>
        ))}
        <Button
          onClick={load}
          className="rounded-full bg-white/5 hover:bg-white/10 border border-white/10 ml-auto"
          data-testid="admin-orders-refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      <div className="space-y-2">
        {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
        {!loading && orders.length === 0 && (
          <p className="text-zinc-500 text-sm">No orders found.</p>
        )}
        {orders.map((o) => (
          <div
            key={o.order_id}
            className="p-3 rounded-xl bg-white/5 border border-white/10"
            data-testid={`admin-order-row-${o.order_id}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold truncate">#{o.order_id.slice(-8)}</p>
                <p className="text-xs text-zinc-400 truncate">
                  ${Number(o.total || 0).toFixed(2)} · buyer {o.buyer_id?.slice(-6)} · vendor {o.vendor_id?.slice(-6)}
                </p>
              </div>
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                {o.status}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {ORDER_STATUSES.filter((s) => s !== o.status).map((s) => (
                <Button
                  key={s}
                  onClick={() => updateStatus(o, s)}
                  className="rounded-full h-8 px-3 text-xs bg-white/5 hover:bg-white/10 border border-white/10"
                  data-testid={`admin-order-${o.order_id}-set-${s}`}
                >
                  Set {s}
                </Button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TicketsTab() {
  const [tickets, setTickets] = useState([]);
  const [status, setStatus] = useState("");
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [forwardToVendor, setForwardToVendor] = useState(false);
  const [templates, setTemplates] = useState({});
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tickets", { params: { status, limit: 100 } });
      setTickets(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/support/reply-templates");
        setTemplates(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const open = async (t) => {
    setActive(t);
    setReply("");
    setForwardToVendor(false);
    try {
      const { data } = await api.get(`/support/tickets/${t.ticket_id}`);
      setThread(data);
    } catch (e) {
      toast.error("Couldn't open ticket");
    }
  };

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${active.ticket_id}/messages`, {
        text: reply.trim(),
        forward_to_vendor: forwardToVendor,
      });
      toast.success(forwardToVendor ? "Reply sent + forwarded to vendor" : "Reply sent");
      setReply("");
      setForwardToVendor(false);
      const { data } = await api.get(`/support/tickets/${active.ticket_id}`);
      setThread(data);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const setStatusOf = async (s) => {
    try {
      await api.post(`/support/tickets/${active.ticket_id}/status`, { status: s });
      toast.success(`Ticket ${s}`);
      const { data } = await api.get(`/support/tickets/${active.ticket_id}`);
      setThread(data);
      load();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const applyTemplate = (key) => {
    const t = templates[key] || "";
    setReply(t.replace("{name}", active?.user_name || "there"));
  };

  if (active && thread) {
    const tt = thread.ticket;
    return (
      <div className="space-y-3" data-testid="ticket-thread">
        <button
          onClick={() => { setActive(null); setThread(null); }}
          className="inline-flex items-center text-sm text-zinc-400 hover:text-white"
          data-testid="ticket-back"
        >
          <ChevronLeft className="w-4 h-4" /> Back to queue
        </button>
        <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-pink-500/20 grid place-items-center font-bold text-pink-200">
              {(tt.user_name || "?")[0]}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold truncate">{tt.subject}</p>
              <p className="text-xs text-zinc-400 truncate">
                {tt.user_name} · {tt.user_email}
              </p>
              <div className="mt-1.5 flex gap-1.5 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-200 border border-pink-400/30">{tt.category}</span>
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 border border-white/10">{tt.status}</span>
                {tt.priority === "high" && (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/15 text-red-200 border border-red-400/30 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Priority
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-1.5">
              {["in_progress", "resolved", "closed"].filter((s) => s !== tt.status).map((s) => (
                <Button
                  key={s}
                  onClick={() => setStatusOf(s)}
                  className="rounded-full h-8 px-3 text-[11px] bg-white/5 hover:bg-white/10 border border-white/10"
                  data-testid={`ticket-set-${s}`}
                >
                  Set {s.replace("_", " ")}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <div className="space-y-2">
          {thread.messages.map((m) => {
            const isAdmin = m.sender_role === "admin" || m.sender_role === "master_admin";
            return (
              <div
                key={m.message_id}
                className={`p-3 rounded-2xl border ${
                  isAdmin
                    ? "bg-cyan-400/[0.07] border-cyan-400/30"
                    : "bg-white/[0.04] border-white/10"
                }`}
                data-testid={`ticket-msg-${m.message_id}`}
              >
                <p className="text-[11px] text-zinc-400 mb-1.5 uppercase tracking-widest font-bold">
                  {m.sender_name} · {m.sender_role}
                  {m.forwarded_to_vendor_id && <span className="ml-2 text-cyan-300">→ forwarded to vendor</span>}
                </p>
                <p className="text-sm whitespace-pre-wrap">{m.text}</p>
              </div>
            );
          })}
        </div>
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {Object.keys(templates).map((k) => (
              <button
                key={k}
                onClick={() => applyTemplate(k)}
                className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-zinc-300"
                data-testid={`ticket-template-${k}`}
              >
                {k}
              </button>
            ))}
          </div>
          <Textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Type your reply…"
            className="bg-white/5 border-white/10 rounded-2xl text-white min-h-[80px]"
            data-testid="ticket-reply-input"
          />
          <div className="flex items-center justify-between gap-2">
            <label className="text-xs text-zinc-300 inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={forwardToVendor}
                disabled={!tt.related_vendor_id}
                onChange={(e) => setForwardToVendor(e.target.checked)}
                data-testid="ticket-forward-toggle"
              />
              Forward to related vendor{!tt.related_vendor_id ? " (no vendor linked)" : ""}
            </label>
            <Button
              onClick={send}
              disabled={!reply.trim() || sending}
              className="rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold h-10 px-4"
              data-testid="ticket-send"
            >
              <Send className="w-3.5 h-3.5 mr-1.5" /> {sending ? "Sending…" : "Send reply"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-tickets">
      <div className="flex flex-wrap gap-2">
        {["", "open", "in_progress", "resolved", "closed"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatus(s)}
            className={`rounded-full h-9 px-3 text-xs font-bold border ${
              status === s ? "bg-pink-500 text-white border-pink-300" : "bg-white/5 border-white/10 text-zinc-300"
            }`}
            data-testid={`admin-tickets-filter-${s || "all"}`}
          >
            {s ? s.replace("_", " ") : "All"}
          </button>
        ))}
        <Button onClick={load} className="ml-auto rounded-full bg-white/5 border border-white/10 hover:bg-white/10" data-testid="admin-tickets-refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
      {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
      {!loading && tickets.length === 0 && <p className="text-zinc-500 text-sm">No tickets.</p>}
      {tickets.map((t) => (
        <button
          key={t.ticket_id}
          onClick={() => open(t)}
          className="w-full text-left p-3 rounded-2xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.07]"
          data-testid={`admin-ticket-row-${t.ticket_id}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-pink-500/20 grid place-items-center text-pink-200 font-bold">
              {(t.user_name || "?")[0]}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold truncate">{t.subject}</p>
              <p className="text-xs text-zinc-400 truncate">{t.user_name} · {t.category}</p>
            </div>
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 border border-white/10">{t.status}</span>
            {t.priority === "high" && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/15 text-red-200 border border-red-400/30">
                <AlertTriangle className="w-3 h-3 inline mr-0.5" />High
              </span>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}

function ModLogsTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/moderation-logs", { params: { limit: 100 } });
      setLogs(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="admin-mod-logs">
      <Button onClick={load} className="rounded-full bg-white/5 hover:bg-white/10 border border-white/10" data-testid="admin-mod-logs-refresh">
        <RefreshCw className="w-4 h-4 mr-2" /> Refresh
      </Button>
      {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
      {!loading && logs.length === 0 && <p className="text-zinc-500 text-sm">No moderation activity yet.</p>}
      {logs.map((l) => (
        <div
          key={l.log_id}
          className="p-3 rounded-2xl bg-white/[0.03] border border-white/10"
          data-testid={`mod-log-${l.log_id}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border font-bold ${
              ["delete"].includes(l.action)
                ? "bg-red-500/15 text-red-200 border-red-400/30"
                : ["ban","suspend","hide"].includes(l.action)
                ? "bg-amber-400/15 text-amber-200 border-amber-400/30"
                : "bg-cyan-400/15 text-cyan-200 border-cyan-400/30"
            }`}>{l.action}</span>
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 border border-white/10">{l.target_type}</span>
            <span className="text-xs text-zinc-300 font-mono truncate">{l.target_id?.slice(0, 18)}…</span>
            <span className="text-[10px] text-zinc-500 ml-auto">{new Date(l.created_at).toLocaleString()}</span>
          </div>
          <p className="text-xs text-zinc-400 mt-1.5">
            by <span className="font-bold text-zinc-200">{l.actor_name}</span> · {l.actor_role}
            {l.reason && <span className="ml-1.5">— {l.reason}</span>}
          </p>
        </div>
      ))}
    </div>
  );
}

function AdminsTab() {
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/admins");
      setAdmins(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/users", { params: { q, limit: 8 } });
        setResults(data.filter((u) => !["admin", "master_admin"].includes(u.role)));
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const promote = async (target) => {
    setBusy(true);
    try {
      if (target?.user_id) {
        await api.post(`/admin/promote/${target.user_id}`);
      } else if (email.trim()) {
        await api.post(`/admin/promote-by-email`, null, { params: { email: email.trim() } });
      }
      toast.success("Admin appointed");
      setEmail("");
      setQ("");
      setResults([]);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Promote failed");
    } finally {
      setBusy(false);
    }
  };

  const demote = async (u) => {
    if (!window.confirm(`Remove admin powers from ${u.name}?`)) return;
    try {
      await api.post(`/admin/demote/${u.user_id}`);
      toast.success("Admin removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Demote failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-admins">
      <div className="rounded-2xl bg-gradient-to-br from-amber-400/15 to-transparent border border-amber-400/30 p-4 space-y-3">
        <p className="text-xs uppercase tracking-widest text-amber-300 font-bold inline-flex items-center gap-1.5">
          <Crown className="w-3.5 h-3.5" /> Appoint a new admin
        </p>
        <div className="flex flex-wrap gap-2">
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            className="h-11 bg-white/5 border-white/10 rounded-full text-white flex-1 min-w-[200px]"
            data-testid="admin-appoint-email"
          />
          <Button
            onClick={() => promote()}
            disabled={!email.trim() || busy}
            className="h-11 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold"
            data-testid="admin-appoint-by-email-btn"
          >
            <UserPlus className="w-4 h-4 mr-1.5" /> Appoint
          </Button>
        </div>
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Or search existing users by name/email…"
            className="pl-9 h-11 bg-white/5 border-white/10 rounded-full text-white"
            data-testid="admin-appoint-search"
          />
        </div>
        {results.length > 0 && (
          <div className="flex flex-col gap-1.5" data-testid="admin-appoint-results">
            {results.map((u) => (
              <div key={u.user_id} className="flex items-center gap-2 p-2 rounded-xl bg-white/5 border border-white/10">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold truncate">{u.name}</p>
                  <p className="text-[11px] text-zinc-400 truncate">{u.email}</p>
                </div>
                <Button
                  onClick={() => promote(u)}
                  disabled={busy}
                  className="h-9 px-3 rounded-full bg-pink-500/15 hover:bg-pink-500/25 text-pink-200 border border-pink-400/30 text-xs"
                  data-testid={`admin-appoint-promote-${u.user_id}`}
                >
                  <UserPlus className="w-3 h-3 mr-1" /> Promote
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <h3 className="text-base font-black tracking-tighter">Roster ({admins.length})</h3>
      <div className="space-y-2">
        {admins.map((u) => (
          <div key={u.user_id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10" data-testid={`admin-roster-${u.user_id}`}>
            <div className={`w-10 h-10 rounded-full grid place-items-center font-bold ${
              u.role === "master_admin" ? "bg-amber-400/20 text-amber-200 border border-amber-400/40" : "bg-pink-500/20 text-pink-200 border border-pink-400/30"
            }`}>
              {(u.name || "?")[0]}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold truncate inline-flex items-center gap-1.5">
                {u.name}
                {u.role === "master_admin" && (
                  <Crown className="w-3.5 h-3.5 text-amber-300" />
                )}
              </p>
              <p className="text-xs text-zinc-400 truncate">{u.email} · {u.role}</p>
            </div>
            {u.role === "admin" && (
              <Button
                onClick={() => demote(u)}
                className="h-9 px-3 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-200 border border-red-400/30 text-xs"
                data-testid={`admin-demote-${u.user_id}`}
              >
                <UserMinus className="w-3 h-3 mr-1" /> Remove
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MasterReports() {
  const [rep, setRep] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/master/reports");
        setRep(data);
      } catch { /* ignore */ }
    })();
  }, []);
  if (!rep) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="master-reports">
      <StatCard label="Open Tickets" value={rep.tickets.open} accent="amber" />
      <StatCard label="In Progress" value={rep.tickets.in_progress} accent="cyan" />
      <StatCard label="Posts" value={rep.posts.total} />
      <StatCard label="Hidden Posts" value={rep.posts.hidden} accent="amber" />
      <StatCard label="New Users 14d" value={rep.trends_14d.new_users} accent="cyan" />
      <StatCard label="New Orders 14d" value={rep.trends_14d.new_orders} accent="cyan" />
      <StatCard label="New Posts 14d" value={rep.trends_14d.new_posts} />
      <StatCard label="Admins" value={rep.roster.admins + 1} accent="amber" />
    </div>
  );
}

function SubscriptionsTab({ isMaster }) {
  const [metrics, setMetrics] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, v] = await Promise.all([
        api.get("/admin/subscriptions/metrics"),
        api.get("/admin/subscriptions/vendors", { params: filter ? { plan: filter } : {} }),
      ]);
      setMetrics(m.data);
      setVendors(v.data.vendors || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Couldn't load subscription data");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-zinc-500 py-8 text-center">Loading…</p>;
  if (!metrics) return null;

  const planColor = {
    trial: "bg-cyan-500/15 text-cyan-200 border-cyan-400/30",
    basic: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30",
    featured: "bg-amber-400/15 text-amber-200 border-amber-400/40",
    inactive: "bg-red-500/15 text-red-200 border-red-400/30",
  };

  return (
    <div className="space-y-5" data-testid="admin-subscriptions-tab">
      {/* MRR + Revenue */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="MRR" value={`$${metrics.mrr_usd.toFixed(2)}`} accent="amber" />
        <StatCard label="Active Basic" value={metrics.active_basic} accent="emerald" />
        <StatCard label="Active Featured" value={metrics.active_featured} accent="amber" />
        <StatCard label="Free Trial" value={metrics.active_trial} accent="cyan" />
        <StatCard label="Inactive" value={metrics.inactive} accent="red" />
        <StatCard label="Cancelling" value={metrics.cancelling} accent="pink" />
        <StatCard label="Founding Vendors" value={metrics.founding_vendors} accent="amber" />
        <StatCard label="VIP Vendors" value={metrics.vip_vendors || 0} accent="amber" />
        <StatCard label="New vendors (30d)" value={metrics.new_vendors_30d} accent="cyan" />
      </div>

      {/* 30-day revenue */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
        <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Revenue · last 30 days</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Subscriptions</p>
            <p className="text-2xl font-black" data-testid="rev-sub-30d">${metrics.subscription_revenue_30d.toFixed(2)}</p>
            <p className="text-[11px] text-zinc-500">{metrics.subscription_charges_30d} charges</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-pink-300 font-bold">Boosts</p>
            <p className="text-2xl font-black" data-testid="rev-boost-30d">${metrics.boost_revenue_30d.toFixed(2)}</p>
            <p className="text-[11px] text-zinc-500">{metrics.boosts_purchased_30d} boosts</p>
          </div>
        </div>
        {Object.keys(metrics.boost_breakdown_30d || {}).length > 0 && (
          <div className="pt-2 border-t border-white/5">
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Boost mix</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(metrics.boost_breakdown_30d).map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs">
                  <Rocket className="w-3 h-3 text-pink-300" />
                  <span className="font-bold capitalize">{k.replace("_", " ")}</span>
                  <span className="text-zinc-400">{v.count} · ${v.revenue.toFixed(2)}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Filter */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {[{k:"",l:"All"},{k:"trial",l:"Trial"},{k:"basic",l:"Basic"},{k:"featured",l:"Featured"},{k:"inactive",l:"Inactive"}].map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            className={`shrink-0 h-8 px-3 rounded-full text-xs font-bold border ${
              filter === t.k ? "bg-pink-500/20 text-pink-200 border-pink-400/40" : "bg-white/5 text-zinc-300 border-white/10"
            }`}
            data-testid={`sub-filter-${t.k || "all"}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {/* Vendor list */}
      <div className="space-y-2">
        {vendors.length === 0 && (
          <p className="text-center text-zinc-500 py-8 text-sm">No vendors match this filter.</p>
        )}
        {vendors.map((v) => {
          const sub = v.subscription || {};
          const plan = sub.plan || "trial";
          return (
            <div key={v.user_id} className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 flex items-center gap-3" data-testid={`sub-row-${v.user_id}`}>
              <div className="w-9 h-9 rounded-full bg-pink-500/20 grid place-items-center text-pink-200 font-bold text-sm shrink-0">
                {(v.vendor_profile?.business_name || v.name || "?")[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold truncate">{v.vendor_profile?.business_name || v.name}</p>
                  {sub.founding_vendor && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-300 to-pink-500 text-black text-[9px] font-black uppercase tracking-widest">
                      <Crown className="w-2.5 h-2.5" /> Founding
                    </span>
                  )}
                </div>
                <p className="text-xs text-zinc-400 truncate">{v.email}</p>
              </div>
              <div className="text-right shrink-0">
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${planColor[plan]}`}>
                  {plan}
                </span>
                {sub.current_period_end && (
                  <p className="text-[10px] text-zinc-500 mt-1">
                    {plan === "trial" || plan === "inactive" ? "Ends" : "Renews"}{" "}
                    {new Date(sub.current_period_end).toLocaleDateString(undefined,{month:"short",day:"numeric"})}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Maintenance — master admin only */}
      {isMaster && (
        <div className="rounded-2xl border border-red-400/30 bg-red-500/10 p-4 space-y-2" data-testid="admin-maintenance">
          <p className="text-xs uppercase tracking-widest text-red-300 font-bold">Maintenance · master only</p>
          <p className="text-sm text-zinc-200">Nuclear actions. There's no undo.</p>
          <button
            onClick={async () => {
              if (!window.confirm("Permanently delete EVERY story from every vendor right now? This cannot be undone.")) return;
              try {
                const { data } = await api.post("/admin/maintenance/wipe-stories");
                toast.success(`Cleared ${data.deleted_count} stories`);
              } catch (e) {
                toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed");
              }
            }}
            className="h-10 px-4 rounded-full bg-red-500/20 hover:bg-red-500/30 border border-red-400/40 text-red-100 text-sm font-bold inline-flex items-center gap-1.5"
            data-testid="wipe-stories-btn"
          >
            <Trash2 className="w-4 h-4" /> Wipe ALL stories now
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
//  Operations Center — the new default tab
// ============================================================
function OperationsTab({ setTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/operations");
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load operations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 30000);  // Refresh every 30s — live feel
    return () => clearInterval(i);
  }, [load]);

  if (loading && !data) return <p className="text-center text-zinc-500 py-8">Loading live ops…</p>;
  if (!data) return null;
  const ops = data.operations || {};

  return (
    <div className="space-y-5" data-testid="operations-tab">
      {/* Live Alert banner */}
      {ops.urgent_alerts > 0 && (
        <div className="relative overflow-hidden rounded-2xl border border-red-400/40 bg-gradient-to-r from-red-500/20 via-amber-500/10 to-transparent p-4 flex items-center gap-3" data-testid="live-alert-banner">
          <span className="absolute left-0 top-0 bottom-0 w-1 bg-red-400 animate-pulse" />
          <Bell className="w-5 h-5 text-red-300 animate-pulse" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-black text-red-300">Urgent</p>
            <p className="text-sm font-bold text-white">{ops.urgent_alerts} alert{ops.urgent_alerts === 1 ? "" : "s"} need attention right now</p>
          </div>
          <button
            onClick={() => setTab("tickets")}
            className="h-9 px-3 rounded-full bg-red-500 hover:bg-red-400 text-white text-xs font-bold"
            data-testid="alert-banner-cta"
          >
            Open <ChevronRight className="inline w-3 h-3 -mt-0.5" />
          </button>
        </div>
      )}

      {/* Live ops tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Open Tickets" value={ops.open_tickets} accent="amber" onClick={() => setTab("tickets")} />
        <StatCard label="Emergency" value={ops.emergency_tickets} accent="red" pulse onClick={() => setTab("tickets")} />
        <StatCard label="Pending Approvals" value={ops.pending_vendor_approvals} accent="cyan" pulse onClick={() => setTab("vendors")} />
        <StatCard label="Vendors Live Now" value={ops.vendors_live_now} accent="pink" hint="Streaming right now" />
        <StatCard label="Staff Online" value={ops.staff_online} accent="emerald" />
        <StatCard label="Awaiting Vendor" value={ops.awaiting_vendor} accent="amber" />
        <StatCard label="Escalated" value={ops.escalated_tickets} accent="red" pulse onClick={() => setTab("tickets")} />
        <StatCard label="Payment Fails 24h" value={ops.payment_failures_24h} accent="red" pulse onClick={() => setTab("subscriptions")} />
        <StatCard label="High Risk Vendors" value={ops.high_risk_vendors} accent="amber" onClick={() => setTab("vendors")} />
      </div>

      {/* Quick Actions Bar */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
        <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2 px-1">Quick actions</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <QuickActionBtn icon={LifeBuoy} label="Review Tickets" onClick={() => setTab("tickets")} testid="qa-tickets" />
          <QuickActionBtn icon={Store} label="Vendor Approvals" onClick={() => setTab("vendors")} testid="qa-vendors" />
          <QuickActionBtn icon={Radio} label="Live Moderation" onClick={() => navigate("/live")} testid="qa-live" />
          <QuickActionBtn icon={Megaphone} label="Send Announcement" onClick={() => toast.info("Announcement composer coming in Staff Portal v2")} testid="qa-announce" />
          <QuickActionBtn icon={Send} label="Open Tickets Inbox" onClick={() => navigate("/support")} testid="qa-inbox" />
          <QuickActionBtn icon={Zap} label="Mod Logs" onClick={() => setTab("logs")} testid="qa-logs" />
        </div>
      </div>

      {/* Pending vendor queue widget */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-black tracking-tighter">Vendor approval queue</h3>
          <button onClick={() => setTab("vendors")} className="text-xs text-pink-300 font-bold inline-flex items-center" data-testid="open-vendor-queue">
            See all <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        {(!data.pending_vendors_preview || data.pending_vendors_preview.length === 0) ? (
          <p className="text-center py-4 text-zinc-500 text-sm">Nobody waiting · you're all caught up</p>
        ) : (
          <div className="space-y-2">
            {data.pending_vendors_preview.map((v) => (
              <button
                key={v.user_id}
                onClick={() => setTab("vendors")}
                className="w-full text-left rounded-xl bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 p-3 flex items-center gap-3"
                data-testid={`pending-vendor-${v.user_id}`}
              >
                <div className="w-9 h-9 rounded-full bg-pink-500/20 text-pink-200 grid place-items-center font-bold text-sm shrink-0">
                  {(v.vendor_profile?.business_name || v.name || "?")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{v.vendor_profile?.business_name || v.name}</p>
                  <p className="text-[11px] text-zinc-400 truncate">{v.email} · {v.vendor_profile?.vendor_type || "food"} · {v.vendor_profile?.location || "no location"}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Live ticket widget */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-black tracking-tighter">Live tickets</h3>
          <button onClick={() => setTab("tickets")} className="text-xs text-pink-300 font-bold inline-flex items-center" data-testid="open-tickets-list">
            See all <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        {(!data.recent_tickets_preview || data.recent_tickets_preview.length === 0) ? (
          <p className="text-center py-4 text-zinc-500 text-sm">All clear · no open tickets</p>
        ) : (
          <div className="space-y-2">
            {data.recent_tickets_preview.map((t) => (
              <div key={t.ticket_id} className="rounded-xl bg-white/[0.04] border border-white/10 p-3" data-testid={`ticket-preview-${t.ticket_id}`}>
                <div className="flex items-center gap-2">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-black ${
                    t.priority === "urgent" ? "bg-red-500/20 text-red-200 border border-red-400/40" :
                    t.status === "escalated" ? "bg-amber-500/20 text-amber-200 border border-amber-400/40" :
                    "bg-cyan-500/20 text-cyan-200 border border-cyan-400/40"
                  }`}>
                    {t.priority === "urgent" ? "URGENT" : t.status}
                  </span>
                  <p className="font-bold text-sm truncate flex-1">{t.subject || "(no subject)"}</p>
                </div>
                <p className="text-[11px] text-zinc-400 truncate mt-1">{t.user_name || t.user_email} · {new Date(t.created_at).toLocaleDateString(undefined,{month:"short",day:"numeric"})}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QuickActionBtn({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="rounded-xl bg-white/[0.05] hover:bg-white/[0.10] border border-white/10 p-3 flex flex-col items-center justify-center gap-1.5 text-center transition-all hover:scale-[1.02] active:scale-[0.98]"
      data-testid={testid}
    >
      <Icon className="w-5 h-5 text-pink-300" />
      <span className="text-[11px] font-bold leading-tight">{label}</span>
    </button>
  );
}

// ============================================================
//  Role Management — CEO ONLY
// ============================================================
function RolesTab() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appointEmail, setAppointEmail] = useState("");
  const [appointRole, setAppointRole] = useState("support");
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/staff");
      setStaff(data.staff || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load staff");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Look up a user_id from email via a tiny helper. We don't have an email-search
  // endpoint that's CEO-only, so use the admin users tab data.
  const appoint = async () => {
    const email = appointEmail.trim().toLowerCase();
    if (!email) return toast.error("Enter the user's email first");
    try {
      // Fetch user list and find the email match
      const { data } = await api.get("/admin/users", { params: { q: email, limit: 5 } });
      const match = (data || []).find((u) => (u.email || "").toLowerCase() === email);
      if (!match) return toast.error("No user with that email");
      await api.post("/admin/staff/assign-role", {
        user_id: match.user_id,
        role: appointRole,
        reason: reason || "",
      });
      toast.success(`Promoted to ${appointRole.replace("_", " ")}`);
      setAppointEmail("");
      setReason("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't appoint");
    }
  };

  const changeRole = async (s, newRole) => {
    const verb = newRole ? `change to ${newRole.replace("_", " ")}` : "remove staff access";
    if (!window.confirm(`${verb} for ${s.email}?`)) return;
    try {
      await api.post("/admin/staff/assign-role", { user_id: s.user_id, role: newRole, reason: "" });
      toast.success(newRole ? "Role updated" : "Staff access removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't update role");
    }
  };

  const ROLE_OPTIONS = [
    { v: "support", l: "Support Staff" },
    { v: "moderator", l: "Moderator" },
    { v: "admin", l: "Admin" },
    { v: "senior_admin", l: "Senior Admin" },
  ];

  if (loading) return <p className="text-center text-zinc-500 py-8">Loading…</p>;

  return (
    <div className="space-y-5" data-testid="roles-tab">
      <div className="rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-300/10 to-pink-500/10 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-amber-300" />
          <p className="text-[10px] uppercase tracking-widest font-black text-amber-300">CEO · Appoint staff</p>
        </div>
        <p className="text-xs text-zinc-300">Promote any existing user to a staff role. They'll instantly get Diamond status, max reputation, and the staff portal access.</p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-2">
          <input
            value={appointEmail}
            onChange={(e) => setAppointEmail(e.target.value)}
            placeholder="user@email.com"
            className="h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm"
            data-testid="appoint-email-input"
          />
          <select
            value={appointRole}
            onChange={(e) => setAppointRole(e.target.value)}
            className="h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm"
            data-testid="appoint-role-select"
          >
            {ROLE_OPTIONS.map((r) => (<option key={r.v} value={r.v}>{r.l}</option>))}
          </select>
          <Button onClick={appoint} className="h-10 px-4 rounded-xl bg-pink-500 hover:bg-pink-400 text-white font-bold text-sm glow-pink" data-testid="appoint-btn">
            Appoint
          </Button>
        </div>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (logged in audit) — optional"
          className="h-9 px-3 w-full rounded-xl bg-white/5 border border-white/10 text-white text-xs"
          data-testid="appoint-reason-input"
        />
      </div>

      <div>
        <h3 className="text-sm font-black tracking-tighter mb-2">Current staff · {staff.length}</h3>
        <div className="space-y-2">
          {staff.map((s) => {
            const isSelfCeo = ["ceo", "master_admin"].includes(s.role);
            return (
              <div key={s.user_id} className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 flex items-center gap-3" data-testid={`staff-row-${s.user_id}`}>
                <div className={`w-9 h-9 rounded-full grid place-items-center font-bold text-sm shrink-0 ${
                  isSelfCeo ? "bg-gradient-to-br from-amber-300 to-pink-500 text-black" : "bg-pink-500/20 text-pink-200"
                }`}>
                  {isSelfCeo ? <Crown className="w-4 h-4" /> : (s.name || s.email || "?")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold truncate">{s.name || s.email}</p>
                    <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full font-black ${
                      isSelfCeo ? "bg-gradient-to-r from-amber-300 to-pink-500 text-black" :
                      s.role === "senior_admin" ? "bg-purple-500/20 text-purple-200 border border-purple-400/40" :
                      s.role === "admin" ? "bg-cyan-500/20 text-cyan-200 border border-cyan-400/40" :
                      s.role === "moderator" ? "bg-emerald-500/20 text-emerald-200 border border-emerald-400/40" :
                      "bg-white/10 text-zinc-200 border border-white/20"
                    }`}>
                      {s.role.replace("_", " ")}
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-400 truncate">{s.email}</p>
                </div>
                {!isSelfCeo && (
                  <div className="flex flex-wrap gap-1 shrink-0">
                    {ROLE_OPTIONS.filter((r) => r.v !== s.role).map((r) => (
                      <button
                        key={r.v}
                        onClick={() => changeRole(s, r.v)}
                        className="h-7 px-2 rounded-full bg-white/5 hover:bg-white/15 text-[10px] font-bold border border-white/10"
                        data-testid={`change-role-${s.user_id}-${r.v}`}
                      >
                        → {r.l}
                      </button>
                    ))}
                    <button
                      onClick={() => changeRole(s, "")}
                      className="h-7 px-2 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-200 text-[10px] font-bold border border-red-400/30"
                      data-testid={`remove-staff-${s.user_id}`}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Staff Portal + Vendor Deep Profile components live in ./admin/*.jsx



export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("operations");
  const [vendorProfileId, setVendorProfileId] = useState(null);
  const [unread, setUnread] = useState({ total: 0, per_channel: {} });

  const STAFF_ROLES_FE = ["ceo", "master_admin", "senior_admin", "admin", "moderator", "support"];
  const isCEO = user?.role === "ceo" || user?.role === "master_admin";
  const isMaster = isCEO; // backwards-compat alias used throughout this file
  const isAdmin = STAFF_ROLES_FE.includes(user?.role);

  const tabs = useMemo(() => (isMaster ? [...BASE_TABS, ...CEO_TABS] : BASE_TABS), [isMaster]);

  // Poll global staff-chat unread for badge + alert pulse on the tab pill
  useEffect(() => {
    if (!isAdmin) return undefined;
    let active = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/staff/unread-summary");
        if (active) setUnread(data || { total: 0, per_channel: {} });
      } catch { /* silent — admin may have lost session */ }
    };
    tick();
    const i = setInterval(tick, 20000);
    return () => { active = false; clearInterval(i); };
  }, [isAdmin]);

  // Pick up ?tab= and ?channel= query params so notifications can deep-link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t && (tabs.some((x) => x.key === t))) setTab(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.length]);

  useEffect(() => {
    if (!loading && user && !isAdmin) {
      toast.error("Staff access only");
      navigate("/feed", { replace: true });
    }
  }, [user, loading, isAdmin, navigate]);

  if (loading || !user) return null;
  if (!isAdmin) return null;

  return (
    <div className="lg:flex lg:max-w-6xl lg:mx-auto lg:gap-6 lg:px-6 lg:py-6 lg:items-start">
      {/* Desktop-only sticky sidebar (hidden on mobile to preserve the existing thumb-zone nav) */}
      <aside
        className="hidden lg:flex lg:flex-col lg:w-56 lg:shrink-0 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto rounded-2xl bg-zinc-950/70 backdrop-blur border border-white/10 p-3 gap-1"
        data-testid="admin-sidebar"
      >
        <div className="px-2 pt-1 pb-2 border-b border-white/10 mb-2">
          <div className="inline-flex items-center gap-1.5">
            {isMaster ? <Crown className="w-4 h-4 text-amber-300" /> : <Shield className="w-4 h-4 text-pink-300" />}
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-black">
              {isMaster ? "CEO Console" : (user.role || "Admin").replace("_", " ")}
            </p>
          </div>
        </div>
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          const showBadge = t.key === "staff_portal" && unread.total > 0;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`relative rounded-xl h-10 px-3 text-xs font-bold uppercase tracking-widest border transition-colors flex items-center gap-2 text-left ${
                active
                  ? "bg-pink-500/20 text-pink-200 border-pink-400/40"
                  : "bg-transparent text-zinc-300 border-transparent hover:bg-white/5"
              }`}
              data-testid={`admin-sidebar-tab-${t.key}`}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              <span className="flex-1 truncate">{t.label}</span>
              {showBadge && (
                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-black grid place-items-center animate-pulse" data-testid="admin-sidebar-staff-chat-badge">
                  {unread.total > 99 ? "99+" : unread.total}
                </span>
              )}
            </button>
          );
        })}
      </aside>

      {/* Main content — identical to before on mobile, flex-1 on desktop */}
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5 pb-24 lg:flex-1 lg:min-w-0 lg:max-w-none lg:mx-0 lg:px-0 lg:py-0 lg:pb-6" data-testid="admin-dashboard">
      <div
        className={`rounded-3xl p-5 ${
          isMaster
            ? "bg-gradient-to-br from-amber-500/15 via-pink-500/10 to-transparent border-2 border-amber-400/40 shadow-[0_0_40px_-12px_rgba(245,158,11,0.4)]"
            : "bg-gradient-to-br from-pink-500/15 to-transparent border border-pink-400/20"
        }`}
        data-testid="admin-header-card"
      >
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-2xl grid place-items-center ${
            isMaster
              ? "bg-gradient-to-br from-amber-400 to-pink-500 text-black"
              : "bg-pink-500/20 text-pink-300 border border-pink-400/40"
          }`}>
            {isMaster ? <Crown className="w-6 h-6" /> : <Shield className="w-5 h-5" />}
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tighter inline-flex items-center gap-2" data-testid="admin-header-title">
              {isMaster ? "Hidden Gem CEO" : `${(user.role || "Admin").replace("_", " ")} Console`}
              {isMaster && (
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-pink-500 text-black font-black" data-testid="master-badge">
                  CEO
                </span>
              )}
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 font-bold inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online
              </span>
            </h1>
            <p className="text-xs text-zinc-300 capitalize">
              {isMaster
                ? "Live operations · staff management · ownership authority"
                : `${(user.role || "").replace("_", " ")} · Diamond access · max reputation`}
            </p>
          </div>
        </div>
        {isMaster && (
          <div className="mt-4 space-y-3">
            <button
              onClick={() => navigate("/admin/economy")}
              className="w-full rounded-2xl p-4 border border-yellow-300/40 bg-gradient-to-r from-yellow-400/15 to-pink-500/15 hover:from-yellow-400/25 hover:to-pink-500/25 transition-colors text-left flex items-center justify-between gap-3"
              data-testid="admin-economy-cta"
            >
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-yellow-300 font-black inline-flex items-center gap-1">
                  <Crown className="w-3 h-3" /> Master Admin
                </p>
                <p className="text-base font-black tracking-tight mt-1">Gift Economy Control Center →</p>
                <p className="text-[11px] text-zinc-300">Catalog · payout split · grants · freezes · revenue analytics</p>
              </div>
            </button>
            <MasterReports />
          </div>
        )}
      </div>

      <div className="lg:hidden flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 sticky top-2 z-20 backdrop-blur supports-[backdrop-filter]:bg-zinc-950/60 rounded-full">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          const showBadge = t.key === "staff_portal" && unread.total > 0;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`relative shrink-0 rounded-full h-9 px-4 text-xs font-bold uppercase tracking-widest border transition-colors flex items-center gap-1.5 ${
                active
                  ? "bg-pink-500/20 text-pink-200 border-pink-400/40"
                  : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"
              }`}
              data-testid={`admin-tab-${t.key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              {showBadge && (
                <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-black grid place-items-center animate-pulse" data-testid="staff-chat-badge">
                  {unread.total > 99 ? "99+" : unread.total}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div>
        {tab === "operations" && <OperationsTab setTab={setTab} />}
        {tab === "staff_portal" && <StaffPortalTab unread={unread} onUnreadChange={setUnread} isCEO={isCEO} />}
        {tab === "stats" && <StatsTab />}
        {tab === "subscriptions" && <SubscriptionsTab isMaster={isMaster} />}
        {tab === "tickets" && <TicketsTab />}
        {tab === "users" && <UsersTab />}
        {tab === "vendors" && <VendorsTab onOpenProfile={setVendorProfileId} />}
        {tab === "products" && <ProductsTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "logs" && <ModLogsTab />}
        {tab === "trash" && <TrashTab isCEO={isCEO} />}
        {tab === "roles" && isMaster && <RolesTab />}
        {tab === "admins" && isMaster && <AdminsTab />}
      </div>

      {vendorProfileId && (
        <VendorDeepProfile
          vendorId={vendorProfileId}
          onClose={() => setVendorProfileId(null)}
          isCEO={isCEO}
        />
      )}
      </div>
    </div>
  );
}
